import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChange } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormsApi } from '@api/forms';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { debounceTime } from 'rxjs/operators';
import english from '../../../../main/maintenances/i18n/en.json';
import spanish from '../../../../main/maintenances/i18n/es.json';
import portuguese from '../../../../main/maintenances/i18n/pt.json';
import { Form, PagedResult } from '../../../interfaces';

export function ValidateAutocomplete(): object {
  return { required: true };
}

const INPUT_FIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => FormsComponent),
  multi: true
};

@Component({
  selector: 'forms-input',
  templateUrl: './forms.component.html',
  styleUrls: ['./forms.component.scss'],
  providers: [INPUT_FIELD_VALUE_ACCESSOR]
})
export class FormsComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Input() hint: boolean;
  @Input() floatLabel: boolean;
  @Input() appearance = 'outline';
  public forms: Array<Form> = [];
  public inputValue = new FormControl();
  public selectedForm: Form;
  public loading = false;

  public options = {
    limit: 25,
    offset: 0,
    orderBy: 'name_asc'
  };

  constructor(private fuseTranslationLoaderService: FuseTranslationLoaderService, private formsApi: FormsApi) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
    if (this.required) {
      this.inputValue.setValidators(ValidateAutocomplete);
    }
  }

  ngOnInit(): void {
    if (this.disabled) {
      this.inputValue.disable();
    }
    this.inputValue.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.forms = [];
      if (value) {
        this.searchForms(value);
      }
    });
  }

  ngOnChanges(changes: { [property: string]: SimpleChange }): void {
    for (const propertyName in changes) {
      if (propertyName === 'disabled') {
        this.disabled = changes[propertyName].currentValue;
        if (this.disabled) {
          this.inputValue.disable();
        } else {
          this.inputValue.enable();
        }
      }
    }
  }

  onChange: (_: any) => void = () => { };
  onTouched: (_: any) => void = () => { };

  searchForms(searchTerm): void {
  this.loading = true;
    this.formsApi
    .search(
      { name_contains: searchTerm && searchTerm.name ? searchTerm.name : searchTerm },
      {
        orderBy: this.options.orderBy,
        limit: this.options.limit,
        offset: this.options.offset,
      }
    )
    .subscribe((result: PagedResult) => {
      this.loading = false;
      this.forms = result.items;
    });
}

  writeValue(formSelected: any): void {
    if (formSelected) {
      this.formsApi.searchOne(formSelected.id).subscribe((form) => {
        this.selectedForm = form;
        this.inputValue.setErrors(null);
      });
    } else {
      this.deselectForm();
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setSelectedForm(): void {
    if (this.inputValue.value) {
      this.selectedForm = this.forms.find((form) => form.id === this.inputValue.value.id);
      this.onChange(this.inputValue.value);
      this.inputValue.setErrors(null);
    }
  }

  deselectForm(): void {
    this.onChange(null);
    this.inputValue.setValue(null);
    this.selectedForm = null;
    this.inputValue.setErrors(null);
  }
}
